// The boards a game may open on.
//
// **A named map, never a board sent over the wire.** Everything a map does —
// ground past the sixty-four, scrap already on it, promotion squares, whether
// the far edge promotes at all — is exactly what `server/rooms.ts` refuses
// from a client, and rightly: `heaps`, `belts` and `cells` between them
// conjure a factory and a board nobody built. An *id* is a different thing. It
// is validated by existing, both ends expand it with this same table, and the
// opening command stays one word long — so a two-player game can be played on
// a board that is not the chess opening without the server having to trust a
// client's idea of what a board is.
//
// **One table, so a map cannot be authorable and unpickable** — the same
// argument `ARMIES` makes in `ai/scenarios.ts`. The name a player reads is
// here beside the ground it lays, rather than in a second list in the menu
// that has to be kept in step.
//
// It lives in `src/engine/rules/` because the *reducer* expands it: two clients
// derive one board from one log, so a table only the client had would be a
// board only the client had.

import { BLACK, CLASSIC_BOUNDS, paddedCells, type Side, square, WHITE } from "./board";
import type { MapGenId } from "./mapgen";
import type { ZoneSlot } from "./zones";
import {
  ZONE_BATTLEFIELD,
  ZONE_DEPOSIT,
  ZONE_DIAMOND,
  ZONE_GOLDEN,
  ZONE_QUARRY
} from "src/shared/zones";

/** How a map is named in a command, a log and a save. */
export type BoardMapId = "classic" | "factory" | "zones" | "frontier" | "expanse";

export interface BoardMap {
  id: BoardMapId;
  /** What the picker calls it. */
  name: string;
  /** One line on what is different about playing it. */
  blurb: string;
  /** Ground past the classic sixty-four. */
  cells: readonly number[];
  /** Scrap already on the board, as `[square, units]`. */
  heaps: readonly (readonly [number, number])[];
  /**
   * Coal lying loose on the board, as `[square, units]` (§31 M2) — what the
   * map's metal is raised with. Loose, so the king's sweep lifts it with the
   * scrap beside it; a deposit there would want a pawn behind its own lines.
   */
  coal: readonly (readonly [number, number])[];
  /**
   * Loose cobble on the board, as `[square, units]` (§31 Q13) — the versus
   * opening's rows. **Laid only when the command names the map**: an absent
   * id is the bare board every test, perft, run and scenario opens on.
   */
  cobble: readonly (readonly [number, number])[];
  /**
   * Promotion squares, as `[square, uses, owner]`.
   *
   * The owner is a side, or -1 for anybody's — which is what a run's revealed
   * squares are. A map whose markers sit *behind* an army has to name one: see
   * `Position.promotionOwner`.
   */
  promotion: readonly (readonly [number, number, number])[];
  /**
   * Whether marching to the far edge promotes (`promotionWall`).
   *
   * False is what makes the markers *the* way to promote rather than a second
   * one — a padded board is square and whole, so without this the wall simply
   * moves out to the padding and a pawn promotes on the edge behind them
   * whether or not it stood on a marker.
   */
  walls: boolean;
  /**
   * Zone slots on White's half (§31 D2), each drawn and grown off the seed
   * and laid again turned half about for Black (`placeMapZones`). Absent on
   * a map without zones.
   */
  zones?: readonly ZoneSlot[];
  /**
   * The generator config that lays this map's ground (§31 D3, `mapgen.ts`)
   * off the seed — zones on White's half, turned half about for Black.
   * Absent on an authored map.
   */
  generate?: MapGenId;
}

/** How far the Factory Map pads the classic board on every side. */
export const FACTORY_PAD = 2;
/** Units in each pile of the two ore lines. */
export const FACTORY_ORE = 4;
/** Coal lying with each pile (§31 M2): a rung of metal per unit of scrap. */
export const FACTORY_COAL = 4;
/** Cobble on each square of the versus rows (§31 Q13): one belt part's worth. */
export const VERSUS_COBBLE = 3;
/** Promotions each marker holds — one per file, spent by the first to arrive. */
export const FACTORY_PROMOTIONS = 1;

/** The files the armies stand on, which both seams run across. */
const armyFiles = (): number[] => {
  const out: number[] = [];
  for (let file = CLASSIC_BOUNDS.minFile; file <= CLASSIC_BOUNDS.maxFile; file++) {
    out.push(file);
  }
  return out;
};

/**
 * The Factory Map's two seams, measured off the classic board rather than
 * written down.
 *
 * The ore lies on the rank immediately behind each army and the markers on the
 * rank behind *that*, which is the outer edge — so the padding is spent
 * exactly twice and moving `FACTORY_PAD` moves both together instead of
 * leaving a seam stranded in the middle of a wider margin.
 */
const oreRanks = [CLASSIC_BOUNDS.minRank - 1, CLASSIC_BOUNDS.maxRank + 1];

/**
 * Whose line of markers is where.
 *
 * **A side marches at the wall behind the *enemy*,** so White's promotion line
 * is the far edge and Black's is the near one. Owned, and that is the whole of
 * why: unowned, the line two ranks behind your own army is reachable by
 * walking your king back and minting pawns onto it — every one of them a
 * queen, on turn two, without a fight. Reported from play as an opening.
 */
const markerLines: readonly (readonly [rank: number, owner: Side])[] = [
  [CLASSIC_BOUNDS.maxRank + FACTORY_PAD, WHITE],
  [CLASSIC_BOUNDS.minRank - FACTORY_PAD, BLACK]
];

const seam = (ranks: readonly number[], amount: number): [number, number][] =>
  ranks.flatMap((rank) => armyFiles().map((file): [number, number] => [square(file, rank), amount]));

const markers = (uses: number): [number, number, number][] =>
  markerLines.flatMap(([rank, owner]) =>
    armyFiles().map((file): [number, number, number] => [square(file, rank), uses, owner])
  );

const CLASSIC: BoardMap = {
  id: "classic",
  name: "Classic Map",
  blurb: "The chess board, a row of cobble before each army's pawns. Metal has to be captured.",
  cells: [],
  heaps: [],
  // No metal until somebody dies, so no coal either: metal past iron needs a
  // fuel source the classic board does not have.
  coal: [],
  // **Versus starts with cobble** (§31 Q13, user ruling 2026-10-26): a row
  // right after each pawn row, so both sides can lay belts and walls early.
  cobble: seam([CLASSIC_BOUNDS.minRank + 2, CLASSIC_BOUNDS.maxRank - 2], VERSUS_COBBLE),
  promotion: [],
  walls: true
};

const FACTORY: BoardMap = {
  id: "factory",
  name: "Factory Map",
  blurb:
    `Two squares of room behind each army, a seam of scrap and coal on it, and ` +
    `promotion only on your own markers — the line along the enemy's far edge.`,
  cells: paddedCells(FACTORY_PAD),
  heaps: seam(oreRanks, FACTORY_ORE),
  coal: seam(oreRanks, FACTORY_COAL),
  cobble: [],
  promotion: markers(FACTORY_PROMOTIONS),
  // The markers are the whole promotion rule here, so the wall is off. See
  // `BoardMap.walls`.
  walls: false
};

/** How wide each flank of the Zone Map is. */
export const ZONE_FLANK = 5;

/** The ground of one flank: `ZONE_FLANK` files beside the classic board, every rank. */
const flank = (from: number): number[] => {
  const out: number[] = [];
  for (let rank = CLASSIC_BOUNDS.minRank; rank <= CLASSIC_BOUNDS.maxRank; rank++) {
    for (let file = from; file < from + ZONE_FLANK; file++) out.push(square(file, rank));
  }
  return out;
};

/**
 * The classic board with a five-file flank each side, zoned (§31 D2). Each
 * flank is two 5×4 slots; White's near pair is drawn off the seed and Black's
 * far pair is the same ground turned about. The ranks are the classic eight,
 * so the wall still promotes. D3's generator replaces it in a run.
 */
const ZONES: BoardMap = {
  id: "zones",
  name: "Zone Map",
  blurb:
    "Chess with a flank of zones each side: quarries and deposits on one, " +
    "gold, diamond or an old battlefield on the other — drawn by the seed, the same for both.",
  cells: [...flank(CLASSIC_BOUNDS.minFile - ZONE_FLANK), ...flank(CLASSIC_BOUNDS.maxFile + 1)],
  heaps: [],
  coal: [],
  cobble: [],
  promotion: [],
  walls: true,
  zones: [
    { at: [CLASSIC_BOUNDS.minFile - ZONE_FLANK, 0, ZONE_FLANK, 4], pool: [ZONE_QUARRY, ZONE_DEPOSIT] },
    { at: [CLASSIC_BOUNDS.maxFile + 1, 0, ZONE_FLANK, 4], pool: [ZONE_GOLDEN, ZONE_DIAMOND, ZONE_BATTLEFIELD] }
  ]
};

/** A generated map: no authored ground, the config lays it all (§31 D3). */
const generated = (
  id: BoardMapId & MapGenId,
  name: string,
  blurb: string,
  walls: boolean
): BoardMap => ({ id, name, blurb, cells: [], heaps: [], coal: [], cobble: [], promotion: [], walls, generate: id });

const FRONTIER = generated(
  "frontier",
  "Frontier Map",
  "Chess with a generated flank each side — quarries, deposits, gold or an old battlefield, " +
    "a new board every seed and the same for both.",
  true
);

// Ground behind the armies, so the far edge is no longer the eighth rank: the
// wall is off and there are no markers. Pawns do not promote here.
const EXPANSE = generated(
  "expanse",
  "Expanse",
  "A generated board past every edge: canyons, islands, gold and diamond, the same for both " +
    "sides. Pawns do not promote.",
  false
);

/** Every map, in the order the picker offers them. Classic first — it is chess. */
export const BOARD_MAPS: readonly BoardMap[] = [CLASSIC, FACTORY, ZONES, FRONTIER, EXPANSE];

/**
 * The map an id names, or undefined.
 *
 * Undefined rather than a fallback to classic: a command naming a map this
 * build does not have is a command whose *board* is unknown, and quietly
 * opening a different one is how two clients replay one log onto two games.
 * The reducer refuses it; an absent id is the classic board and is not an
 * unknown one.
 */
export const boardMap = (id?: string): BoardMap | undefined =>
  id === undefined ? CLASSIC : BOARD_MAPS.find((map) => map.id === id);

/** Is this a map this build knows? What the server validates a command with. */
export const isBoardMap = (id: unknown): id is BoardMapId =>
  typeof id === "string" && BOARD_MAPS.some((map) => map.id === id);
